import React, { useState, useCallback, useRef } from 'react';
import { ArrowRight, CheckCircle, TrendingDown, Clock, Download } from 'lucide-react';
import { useBooking } from '../context/BookingContext';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import { saveDiagnosticSubmission } from '../lib/diagnosticSubmission';
import { downloadPdfFromElement } from '../lib/diagnosticExport';
import DiagnosticPdfSheet from './DiagnosticPdfSheet';
import { NICHE_LABELS, calculateReport } from '../lib/diagnosticLossModel';
import './DiagnosticChat.css';

// ─── Business types ───────────────────────────────────────────────────────────

const BUSINESS_TYPES = [
  { value: 'medspa',      label: 'Medspa / cosmetic clinic' },
  { value: 'insurance',   label: 'Insurance broker' },
  { value: 'mortgage',    label: 'Mortgage broker' },
  { value: 'vet',         label: 'Veterinary clinic' },
  { value: 'law',         label: 'Law firm' },
  { value: 'accounting',  label: 'Accounting firm' },
  { value: 'gym',         label: 'Gym / fitness studio' },
  { value: 'realestate',  label: 'Real estate team' },
  { value: 'dealership',  label: 'Auto dealership' },
  { value: 'trades',      label: 'Trades: HVAC, plumbing, electrical' },
  { value: 'medical',     label: 'Medical practice', comingSoon: true },
];

const TOTAL_STEPS = 6; // niche + 5 questions

// ─── Questions ────────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: 'team_size',
    question: () => 'About how many people work in the business (including you)?',
    gauge: true,
    options: [
      { value: 'solo',   label: 'Solo / micro', sub: '1–3 people' },
      { value: 'small',  label: 'Small team',   sub: '4–10' },
      { value: 'medium', label: 'Growing',      sub: '11–30' },
      { value: 'large',  label: 'Larger org',   sub: '31+' },
    ],
  },
  {
    id: 'inquiry_handling',
    question: () => "When a new inquiry comes in, what actually happens?",
    options: [
      { value: 'fast',    label: 'Someone responds within the hour',   sub: 'Consistent, every time' },
      { value: 'sameday', label: 'Usually same day, a few hours',     sub: 'Depends on who\'s in' },
      { value: 'slow',    label: 'Could be next day or later',         sub: 'We get busy' },
      { value: 'slips',   label: 'Some slip through the cracks',       sub: 'Honestly, yes' },
    ],
  },
  {
    id: 'after_hours',
    question: () => "Outside your business hours, what happens when someone reaches out?",
    options: [
      { value: 'handled',   label: 'We have something in place for it' },
      { value: 'voicemail', label: 'Voicemail or a generic auto-reply' },
      { value: 'miss',      label: 'Most don\'t hear from us until we notice' },
      { value: 'nothing',   label: 'Nothing. They wait until we open' },
    ],
  },
  {
    id: 'follow_up',
    question: (niche) => `When someone shows interest but doesn't ${NICHE_LABELS[niche]?.verb ?? 'book'}, what happens next?`,
    options: [
      { value: 'auto',      label: 'Automated follow-up goes out' },
      { value: 'manual',    label: 'Someone on the team reaches out' },
      { value: 'sometimes', label: 'We try, but it\'s inconsistent' },
      { value: 'never',     label: 'We move on. Nothing goes out' },
    ],
  },
  {
    id: 'monthly_inquiries',
    question: (niche) => `Roughly how many new ${NICHE_LABELS[niche]?.unit ?? 'inquiries'} reach you each month?`,
    options: [
      { value: 'low',   label: 'Under 10' },
      { value: 'mid',   label: '10–30' },
      { value: 'high',  label: '30–60' },
      { value: 'vhigh', label: '60+' },
    ],
  },
];

function fmt(n) {
  return n.toLocaleString('en-CA');
}

function isValidEmail(value) {
  const v = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DiagnosticChat() {
  const { openCalendly } = useBooking();

  // phase: 'intro' | 'niche' | 'quiz' | 'coming_soon' | 'result'
  const [phase, setPhase]     = useState('intro');
  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({});
  const [report, setReport]   = useState(null);
  const [exiting, setExiting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const pdfSheetRef = useRef(null);

  const total = QUESTIONS.length;
  const niche = answers.business_type;

  const transition = useCallback((fn) => {
    setExiting(true);
    setTimeout(() => { setExiting(false); fn(); }, 200);
  }, []);

  const handleStart = useCallback(() => {
    transition(() => setPhase('niche'));
  }, [transition]);

  const handleNicheSelect = useCallback((value, comingSoon) => {
    if (comingSoon) {
      transition(() => setPhase('coming_soon'));
      return;
    }
    const newAnswers = { ...answers, business_type: value };
    setAnswers(newAnswers);
    transition(() => { setPhase('quiz'); setStep(1); });
  }, [answers, transition]);

  const handleAnswer = useCallback((qId, value) => {
    const newAnswers = { ...answers, [qId]: value };
    setAnswers(newAnswers);

    transition(() => {
      if (step < total) {
        setStep(step + 1);
      } else {
        const r = calculateReport(newAnswers);
        setReport(r);
        setPhase('result');
      }
    });
  }, [answers, step, total, transition]);

  const handleReset = useCallback(() => {
    transition(() => {
      setPhase('intro');
      setStep(0);
      setAnswers({});
      setReport(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setSaveStatus('idle');
      setSaveError('');
    });
  }, [transition]);

  const canDownloadSummary =
    Boolean(firstName.trim()) && Boolean(lastName.trim()) && isValidEmail(email);

  const handleDownloadSummary = useCallback(async () => {
    if (!report) return;
    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    if (!fn || !ln || !isValidEmail(em)) return;
    setSaveError('');
    setSaveStatus('saving');
    if (isSupabaseConfigured()) {
      const res = await saveDiagnosticSubmission({
        firstName: fn,
        lastName: ln,
        email: em,
        answers,
        report,
      });
      if (!res.ok) {
        setSaveStatus('error');
        setSaveError(res.error || 'Could not save. Try again.');
        return;
      }
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const node = pdfSheetRef.current;
    if (!node) {
      setSaveStatus('error');
      setSaveError('Could not prepare PDF.');
      return;
    }
    try {
      const safe = [fn, ln].join('-').replace(/\s+/g, '-') || 'diagnostic';
      await downloadPdfFromElement(node, `cure8-diagnostic-${safe}-${Date.now()}.pdf`);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
      setSaveError('Could not create PDF. Try again.');
    }
  }, [answers, report, firstName, lastName, email]);

  const currentQ = QUESTIONS[step - 1];

  return (
    <>
    <section className="diag-section" id="diagnosis">
      <div className="container">

        <div className="diag-header">
          <p className="diag-eyebrow">Free · 2 min</p>
          <h2 className="diag-title">Where are you losing leads?</h2>
          <p className="diag-desc">Five questions. Name and email at the end to download your summary.</p>
        </div>

        <div className={`diag-card-wrap ${phase === 'niche' ? 'diag-card-wrap-niche' : ''}`}>
          <div
            className={`diag-card ${exiting ? 'diag-exit' : 'diag-enter'} ${phase === 'niche' ? 'diag-card-niche' : ''} ${phase === 'result' ? 'diag-card-result' : 'diag-card-survey'}`}
          >

            {/* ── Intro ── */}
            {phase === 'intro' && (
              <div className="diag-step diag-step-intro">
                <div className="diag-icon-wrap">
                  <TrendingDown size={26} />
                </div>
                <p className="diag-intro-q">See what to fix first, then spend smarter.</p>
                <p className="diag-intro-sub">2 min · 5 questions</p>
                <button className="btn diag-start-btn" onClick={handleStart}>
                  Show me <ArrowRight size={15} />
                </button>
              </div>
            )}

            {/* ── Niche select ── */}
            {phase === 'niche' && (
              <div className="diag-step diag-step-niche">
                <div className="diag-progress">
                  <span className="diag-dot diag-dot-active" />
                  {QUESTIONS.map((_, i) => <span key={i} className="diag-dot" />)}
                  <span className="diag-step-count">1 of {TOTAL_STEPS}</span>
                </div>
                <p className="diag-step-q diag-step-q-niche">What type of business do you run?</p>
                <div className="diag-niche-scroll" role="region" aria-label="Business types">
                  <div className="diag-options diag-options-niche">
                    {BUSINESS_TYPES.map((bt) => (
                      <button
                        key={bt.value}
                        type="button"
                        className={`diag-option diag-option-niche${bt.comingSoon ? ' diag-option-soon' : ''}`}
                        onClick={() => handleNicheSelect(bt.value, bt.comingSoon)}
                      >
                        <span className="diag-option-label">{bt.label}</span>
                        {bt.comingSoon && <span className="diag-soon-tag">Coming soon</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Quiz questions ── */}
            {phase === 'quiz' && currentQ && (
              <div className="diag-step diag-step-quiz">
                <div className="diag-progress">
                  <span className="diag-dot diag-dot-done" />
                  {QUESTIONS.map((_, i) => (
                    <span key={i} className={`diag-dot ${i < step ? 'diag-dot-done' : i === step - 1 ? 'diag-dot-active' : ''}`} />
                  ))}
                  <span className="diag-step-count">{step + 1} of {TOTAL_STEPS}</span>
                </div>
                <p className="diag-step-q">{currentQ.question(niche)}</p>
                <div className={`diag-options${currentQ.gauge ? ' diag-options--gauge' : ''}`}>
                  {currentQ.options.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`diag-option${currentQ.gauge ? ' diag-option--gauge' : ''}`}
                      onClick={() => handleAnswer(currentQ.id, opt.value)}
                    >
                      <span className="diag-option-label">{opt.label}</span>
                      {opt.sub && <span className="diag-option-sub">{opt.sub}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Coming soon ── */}
            {phase === 'coming_soon' && (
              <div className="diag-step diag-step-intro">
                <div className="diag-icon-wrap diag-icon-clock">
                  <Clock size={26} />
                </div>
                <p className="diag-intro-q">Medical practices are next on our list.</p>
                <p className="diag-intro-sub">
                  We're building a tailored diagnostic for clinics and practices. It is next on the list.
                  In the meantime, book a free discovery call and we'll walk through your gaps directly.
                </p>
                <button className="btn diag-start-btn" onClick={() => { openCalendly(); }}>
                  Book a free discovery call <ArrowRight size={15} />
                </button>
                <button className="diag-reset" onClick={handleReset}>Back</button>
              </div>
            )}

            {/* ── Result ── */}
            {phase === 'result' && report && (
              <div className="diag-result">
                <div className="diag-result-scroll">
                  <div className="diag-result-metrics">
                    <div className="diag-metric">
                      <span className="diag-metric-label">Estimated monthly leakage</span>
                      <span className="diag-metric-value">${fmt(report.monthlyLeakage)}<em>/mo</em></span>
                      <span className="diag-metric-sub">${fmt(report.annualLeakage)} per year</span>
                      {report.teamLabel && (
                        <span className="diag-metric-context">Scaled for ~{report.teamLabel}</span>
                      )}
                    </div>
                    <div className="diag-metric-divider" />
                    <div className="diag-metric">
                      <span className="diag-metric-label">Recoverable with our help</span>
                      <span className="diag-metric-value diag-metric-green">~${fmt(report.recoverable)}<em>/mo</em></span>
                    </div>
                  </div>

                  <div className="diag-gaps">
                    <p className="diag-gaps-label">Where it's coming from</p>
                    {report.gaps.map((gap, i) => (
                      <div key={i} className="diag-gap">
                        <div className="diag-gap-row">
                          <span className="diag-gap-title">{gap.title}</span>
                          <span className="diag-gap-cost">${fmt(gap.cost)}/mo</span>
                        </div>
                        <div className="diag-gap-fix">
                          <CheckCircle size={11} />
                          {gap.fix}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="diag-result-package">
                    <span className="diag-package-label">Recommended starting point</span>
                    <span className="diag-package-name">
                      {report.recommended === 'pro' ? 'The Pro' : 'The Pilot'}
                    </span>
                    <span className="diag-package-desc">
                      {report.recommended === 'pro'
                        ? 'Full system build for businesses with multiple gaps affecting daily revenue.'
                        : 'One focused fix, built and live in two weeks. Most clients recover the cost in the first month.'}
                    </span>
                  </div>

                  <div className="diag-save-block">
                    <div className="diag-save-fields">
                    <label className="diag-save-field">
                      <span className="diag-save-label">First name</span>
                      <input
                        type="text"
                        className="diag-save-input"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        placeholder="First name"
                        required
                      />
                    </label>
                    <label className="diag-save-field">
                      <span className="diag-save-label">Last name</span>
                      <input
                        type="text"
                        className="diag-save-input"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        placeholder="Last name"
                        required
                      />
                    </label>
                    <label className="diag-save-field diag-save-field-full">
                      <span className="diag-save-label">Email</span>
                      <input
                        type="email"
                        className="diag-save-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        placeholder="you@company.com"
                        required
                      />
                    </label>
                    </div>
                    <div className="diag-save-actions">
                      <button
                        type="button"
                        className="btn diag-save-primary"
                        onClick={handleDownloadSummary}
                        disabled={!canDownloadSummary || saveStatus === 'saving'}
                        title={
                          !canDownloadSummary
                            ? 'Enter first name, last name, and email to download'
                            : undefined
                        }
                      >
                        <Download size={15} aria-hidden />
                        {saveStatus === 'saving' ? 'Preparing…' : 'Download PDF'}
                      </button>
                    </div>
                    {saveStatus === 'error' && <p className="diag-save-error">{saveError}</p>}
                  </div>
                </div>

                <div className="diag-result-footer diag-result-footer--pinned">
                  <button type="button" className="btn diag-cta-btn" onClick={openCalendly}>
                    Book a free discovery session <ArrowRight size={15} />
                  </button>
                  <p className="diag-cta-note">
                    We'll walk through these findings and tell you exactly what we'd build first.
                  </p>
                  <button type="button" className="diag-reset" onClick={handleReset}>Start over</button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
    {phase === 'result' && report && (
      <div className="diag-pdf-sheet-host" aria-hidden="true">
        <DiagnosticPdfSheet
          ref={pdfSheetRef}
          report={report}
          answers={answers}
          firstName={firstName.trim()}
          lastName={lastName.trim()}
          email={email.trim()}
        />
      </div>
    )}
    </>
  );
}
